import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwitcherButton from '../../../Buttons/CommonSwitcher';
import CommonButton from '../../../Buttons/CommonButton';
import Hint from '../../../Tools/Hint';
import {
    isNumber,
    formatNumber,
    numberWithoutCommas,
} from '../../../../utils/functions';

const i18nBase = 'sections.campaigns.new-campaign.wizard-basics.dailyCaps';

// Local Y-M-D, consistent with dateToIso8601 (backend day keys)
const toDayKey = (date) =>
    `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

const buildDayKeys = (periodValue) => {
    if (!periodValue?.[0] || !periodValue?.[1]) return [];
    const start = new Date(periodValue[0]);
    const end = new Date(periodValue[1]);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];

    const days = [];
    const cursor = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
    );
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cursor <= last && days.length < 366) {
        days.push(toDayKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return days;
};

const DailyCaps = ({
    dailyCaps,
    timing,
    budget,
    totalSpent = 0,
    selectedCurrency,
    updateBasics,
}) => {
    const { t, i18n } = useTranslation();
    const [assignAllValue, setAssignAllValue] = useState('');

    const caps = useMemo(() => dailyCaps?.caps ?? {}, [dailyCaps?.caps]);
    const isActive = Boolean(dailyCaps?.isActive);
    const isAsap = timing?.strategy === 'asap';

    const dayKeys = useMemo(
        () => buildDayKeys(timing?.periodValue),
        [timing?.periodValue?.[0], timing?.periodValue?.[1]]
    );

    // Days already elapsed (spent campaigns keep their locked start in the
    // past) are shown read-only and left out of budget distribution.
    const todayKey = useMemo(() => toDayKey(new Date()), []);
    const upcomingDayKeys = useMemo(
        () => dayKeys.filter((key) => key >= todayKey),
        [dayKeys, todayKey]
    );
    const isPastDay = (dayKey) => dayKey < todayKey;

    const change = (attribute, value) => {
        updateBasics('dailyCaps', {
            ...(dailyCaps ?? { isActive: false, caps: {} }),
            [attribute]: value,
        });
    };

    // Range shrank: discard days no longer inside the campaign period
    useEffect(() => {
        if (!dayKeys.length) return;
        const staleKeys = Object.keys(caps).filter(
            (key) => !dayKeys.includes(key)
        );
        if (staleKeys.length === 0) return;

        const prunedCaps = { ...caps };
        staleKeys.forEach((key) => delete prunedCaps[key]);
        change('caps', prunedCaps);
    }, [dayKeys]);

    // ASAP pacing spends as fast as possible, so per-day caps make no sense:
    // switch them off but keep the values in case the strategy changes back.
    useEffect(() => {
        if (isAsap && isActive) {
            change('isActive', false);
        }
    }, [isAsap, isActive]);

    const totalBudget = Number(numberWithoutCommas(budget?.budget)) || 0;
    const spent = Number(numberWithoutCommas(totalSpent)) || 0;
    const hasSpent = spent > 0;
    const remainingBudget = Math.max(totalBudget - spent, 0);
    const assignedTotal = upcomingDayKeys.reduce(
        (sum, key) => sum + (Number(caps[key]) || 0),
        0
    );
    const difference = remainingBudget - assignedTotal;

    const handleCapChange = (dayKey, rawValue) => {
        const value = rawValue.replace(/^-/, '');
        if (value === '') {
            // Empty input = no cap for that day (out of the map)
            const newCaps = { ...caps };
            delete newCaps[dayKey];
            change('caps', newCaps);
            return;
        }
        if (!isNumber(value)) return;
        change('caps', {
            ...caps,
            [dayKey]: Number(numberWithoutCommas(value)),
        });
    };

    // Past-day caps are preserved untouched; only upcoming days are rewritten
    const applyToUpcomingDays = (valueForDay) => {
        const newCaps = { ...caps };
        upcomingDayKeys.forEach((key, index) => {
            newCaps[key] = valueForDay(index);
        });
        change('caps', newCaps);
    };

    const distributeEvenly = () => {
        const daysCount = upcomingDayKeys.length;
        if (!daysCount || remainingBudget <= 0) return;
        const base = Math.floor(remainingBudget / daysCount);
        const remainder = Math.round(remainingBudget - base * (daysCount - 1));
        applyToUpcomingDays((index) =>
            index === daysCount - 1 ? remainder : base
        );
    };

    const assignToAll = () => {
        if (assignAllValue === '') return;
        const value = Number(numberWithoutCommas(assignAllValue));
        applyToUpcomingDays(() => value);
    };

    const dayLabel = (dayKey) => {
        const [year, month, day] = dayKey.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString(
            i18n.language,
            { weekday: 'short', day: '2-digit', month: '2-digit' }
        );
    };

    const currencySymbol = selectedCurrency?.symbol ?? '';

    if (dayKeys.length === 0) return null;

    return (
        <div className="module rounded m-1 mb-4">
            <div className="header flex justify-between content-center">
                <div className="title text-xl font-extralight">
                    {t(`${i18nBase}.title`)}
                </div>
                <div className="brief flex items-center gap-2">
                    {isAsap && (
                        <Hint
                            title={t(`${i18nBase}.title`)}
                            text={t(`${i18nBase}.asap-disabled`)}
                            type={'info'}
                        />
                    )}
                    <div
                        className={
                            isAsap ? 'opacity-50 pointer-events-none' : ''
                        }
                        aria-disabled={isAsap}>
                        <SwitcherButton
                            onToggle={() =>
                                !isAsap && change('isActive', !isActive)
                            }
                            status={isActive}
                            labelOn={t(`${i18nBase}.labelOn`)}
                            labelOff={t(`${i18nBase}.labelOff`)}
                            testId="daily-caps-toggle"
                        />
                    </div>
                </div>
            </div>

            <div className={`${isActive ? '' : 'hidden'} body rounded-sm p-1`}>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-3">
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                        <CommonButton
                            styleType="secondary"
                            label={t(`${i18nBase}.distribute-evenly`)}
                            onClick={distributeEvenly}
                            testId="daily-caps-distribute-evenly"
                        />
                        <div className="flex items-end gap-2">
                            <div className="relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                                    <span className="text-gray-500 sm:text-sm">
                                        {currencySymbol}
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    autoComplete="off"
                                    className="focus:ring-0 focus:outline-none block w-28 pl-7 sm:text-sm border-gray-300 rounded-md"
                                    placeholder="0"
                                    value={assignAllValue}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /^-/,
                                            ''
                                        );
                                        if (value === '' || isNumber(value)) {
                                            setAssignAllValue(
                                                value === ''
                                                    ? ''
                                                    : formatNumber(
                                                          numberWithoutCommas(
                                                              value
                                                          ),
                                                          'withcommas'
                                                      )
                                            );
                                        }
                                    }}
                                    data-testid="daily-caps-assign-all-input"
                                />
                            </div>
                            <CommonButton
                                styleType="secondary"
                                label={t(`${i18nBase}.assign-to-all`)}
                                onClick={assignToAll}
                                testId="daily-caps-assign-all"
                            />
                        </div>
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">
                        {t(
                            `${i18nBase}.${
                                hasSpent
                                    ? 'remaining-budget-label'
                                    : 'budget-label'
                            }`
                        )}
                        :{' '}
                        <span className="font-medium text-gray-700">
                            {currencySymbol}
                            {formatNumber(remainingBudget, 'withcommas')}
                        </span>
                        {' · '}
                        {t(`${i18nBase}.assigned-label`)}:{' '}
                        <span className="font-medium text-gray-700">
                            {currencySymbol}
                            {formatNumber(assignedTotal, 'withcommas')}
                        </span>
                        {' · '}
                        {t(`${i18nBase}.difference-label`)}:{' '}
                        <span
                            className={`font-medium ${
                                difference < 0
                                    ? 'text-yellow-600'
                                    : 'text-gray-700'
                            }`}>
                            {currencySymbol}
                            {formatNumber(difference, 'withcommas')}
                        </span>
                    </div>
                </div>

                <div className="text-xs text-gray-400 mb-2">
                    {t(`${i18nBase}.empty-hint`)}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                    {dayKeys.map((dayKey) => (
                        <div
                            key={dayKey}
                            className={`border border-gray-200 rounded-md p-1 ${
                                isPastDay(dayKey) ? 'opacity-50' : ''
                            }`}>
                            <div className="text-xs text-gray-500 capitalize mb-1 truncate">
                                {dayLabel(dayKey)}
                            </div>
                            <input
                                type="text"
                                autoComplete="off"
                                disabled={isPastDay(dayKey)}
                                className="focus:ring-0 focus:outline-none block w-full sm:text-sm text-sm border-gray-300 rounded-md px-1 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                placeholder={t(`${i18nBase}.no-cap`)}
                                value={
                                    caps[dayKey] !== undefined
                                        ? formatNumber(
                                              caps[dayKey],
                                              'withcommas'
                                          )
                                        : ''
                                }
                                onChange={(e) =>
                                    handleCapChange(dayKey, e.target.value)
                                }
                                data-testid={`daily-cap-${dayKey}`}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DailyCaps;
